/** Public projection of the published MCPAPI Codex receipt. A valid receipt is
 * display evidence only; it never grants authority to read/cancel a run. */
export interface CodexReceipt {
  run_id: string;
  receipt_id: string;
  runtime: 'codex';
  status: 'submitted' | 'queued' | 'running' | 'completed' | 'failed' | 'cancel_requested' | 'cancelled' | 'reconciliation_pending';
  session_observed: boolean;
  result: string | null;
  usage_state: 'unknown' | 'known';
  usage_complete: boolean;
  settlement_state: 'reserved' | 'settled' | 'uncertain';
  charged_credits: number | null;
  physical_cessation_verified: boolean;
  /** Per-run metering, present only when the server sent it. null or absent = not reported (never 0). */
  tier?: 'light' | 'standard' | 'deep' | null;
  model_id?: string | null;
  input_tokens?: number | null;
  cached_input_tokens?: number | null;
  output_tokens?: number | null;
  priced_cost_usd?: number | null;
  /** Run timing, present only when the server sent it. null or absent = not reported (never 0). */
  started_at?: string | null;
  finished_at?: string | null;
  wall_seconds?: number | null;
  /** MC settlement lifecycle; null or absent = not reported. Terminal: settled, released, refused. */
  settlement_status?: 'reserved' | 'settlement_pending' | 'settling' | 'settled' | 'released' | 'refused' | null;
  /** Final settled amount, present only once settled. */
  settled_amount?: Readonly<{ usd: number; credits: number; rate_table_version: string }> | null;
}

type Metering = Pick<CodexReceipt, 'tier' | 'model_id' | 'input_tokens' | 'cached_input_tokens' | 'output_tokens' | 'priced_cost_usd'
  | 'started_at' | 'finished_at' | 'wall_seconds' | 'settlement_status' | 'settled_amount'>;
const settlementStatuses = new Set(['reserved', 'settlement_pending', 'settling', 'settled', 'released', 'refused']);
const instantOk = (v: unknown): boolean => v === null || (typeof v === 'string' && v.length <= 64 && Number.isFinite(Date.parse(v)));
const countOk = (v: unknown): boolean => v === null || (typeof v === 'number' && Number.isSafeInteger(v) && v >= 0);
/** Copies only the metering fields the response carries; a present field of the wrong type is coercion
 * and rejects the whole receipt (undefined result). */
function decodeMetering(r: Record<string, unknown>): Metering | undefined {
  const out: Metering = {};
  if ('tier' in r) { if (r.tier !== null && r.tier !== 'light' && r.tier !== 'standard' && r.tier !== 'deep') return undefined; out.tier = r.tier as Metering['tier']; }
  if ('model_id' in r) { if (r.model_id !== null && (typeof r.model_id !== 'string' || !r.model_id || r.model_id.length > 200)) return undefined; out.model_id = r.model_id as string | null; }
  for (const key of ['input_tokens', 'cached_input_tokens', 'output_tokens'] as const) {
    if (key in r) { if (!countOk(r[key])) return undefined; out[key] = r[key] as number | null; }
  }
  if ('priced_cost_usd' in r) {
    const v = r.priced_cost_usd;
    if (v !== null && (typeof v !== 'number' || !Number.isFinite(v) || v < 0)) return undefined;
    out.priced_cost_usd = v as number | null;
  }
  for (const key of ['started_at', 'finished_at'] as const) {
    if (key in r) { if (!instantOk(r[key])) return undefined; out[key] = r[key] as string | null; }
  }
  if ('wall_seconds' in r) {
    const v = r.wall_seconds;
    if (v !== null && (typeof v !== 'number' || !Number.isFinite(v) || v < 0)) return undefined;
    out.wall_seconds = v as number | null;
  }
  if ('settlement_status' in r) {
    if (r.settlement_status !== null && (typeof r.settlement_status !== 'string' || !settlementStatuses.has(r.settlement_status))) return undefined;
    out.settlement_status = r.settlement_status as Metering['settlement_status'];
  }
  if ('settled_amount' in r) {
    const a = r.settled_amount;
    if (a !== null) {
      if (!a || typeof a !== 'object' || Array.isArray(a)) return undefined;
      const v = a as Record<string, unknown>;
      if (Object.keys(v).length !== 3 || typeof v.usd !== 'number' || !Number.isFinite(v.usd) || v.usd < 0
        || !countOk(v.credits) || v.credits === null || typeof v.rate_table_version !== 'string' || !v.rate_table_version) return undefined;
      out.settled_amount = { usd: v.usd, credits: v.credits as number, rate_table_version: v.rate_table_version };
    } else out.settled_amount = null;
  }
  // Wall time without a start, or an end before the start, is contradictory evidence.
  if (typeof out.wall_seconds === 'number' && !out.started_at) return undefined;
  if (out.started_at && out.finished_at && Date.parse(out.finished_at) < Date.parse(out.started_at)) return undefined;
  return out;
}

const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
const statuses = new Set(['submitted', 'queued', 'running', 'completed', 'failed', 'cancel_requested', 'cancelled', 'reconciliation_pending']);

/** Reject incomplete or contradictory evidence and copy only public fields.
 * Do not coerce unknown usage to zero or requested cancellation to cessation. */
export function decodeCodexReceipt(value: unknown, expectedRunId?: string): CodexReceipt | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const r = value as Record<string, unknown>;
  if (typeof r.run_id !== 'string' || !uuid.test(r.run_id)
    || (expectedRunId !== undefined && r.run_id !== expectedRunId)
    || typeof r.receipt_id !== 'string' || !uuid.test(r.receipt_id) || r.runtime !== 'codex'
    || typeof r.status !== 'string' || !statuses.has(r.status)
    || typeof r.session_observed !== 'boolean' || typeof r.usage_complete !== 'boolean'
    || typeof r.physical_cessation_verified !== 'boolean'
    || (r.result !== null && (typeof r.result !== 'string' || r.result.length > 100_000))
    || (r.usage_state !== 'unknown' && r.usage_state !== 'known')
    || (r.settlement_state !== 'reserved' && r.settlement_state !== 'settled' && r.settlement_state !== 'uncertain')
    || (r.charged_credits !== null && (typeof r.charged_credits !== 'number'
      || !Number.isSafeInteger(r.charged_credits) || r.charged_credits < 0))) return null;
  const terminal = ['completed', 'failed', 'cancelled'].includes(r.status);
  if ((!r.session_observed && r.result !== null)
    || (r.usage_state === 'unknown' && (r.usage_complete || r.charged_credits !== null || r.settlement_state === 'settled'))
    || (r.settlement_state !== 'settled' && r.charged_credits !== null)
    // A settled receipt always states its settled amount.
    || (r.settlement_state === 'settled' && r.charged_credits === null)
    || (r.settlement_state === 'settled' && (!r.usage_complete || !r.session_observed || !r.physical_cessation_verified || !terminal))
    || (r.physical_cessation_verified && (!r.session_observed || !terminal))) return null;
  const metering = decodeMetering(r);
  if (!metering) return null;
  // MC's settlement consistency: status settled <=> state settled; an amount only once settled, and always then.
  const status = metering.settlement_status;
  if ((status != null && (status === 'settled') !== (r.settlement_state === 'settled'))
    || (metering.settled_amount != null && r.settlement_state !== 'settled')
    || (status === 'settled' && metering.settled_amount == null)) return null;
  // Unknown usage never carries token counts or a priced cost.
  if (r.usage_state === 'unknown' && [metering.input_tokens, metering.cached_input_tokens, metering.output_tokens, metering.priced_cost_usd]
    .some((v) => v !== null && v !== undefined)) return null;
  return {
    ...metering,
    run_id: r.run_id, receipt_id: r.receipt_id, runtime: 'codex', status: r.status as CodexReceipt['status'],
    session_observed: r.session_observed, result: r.result as string | null,
    usage_state: r.usage_state as CodexReceipt['usage_state'], usage_complete: r.usage_complete,
    settlement_state: r.settlement_state as CodexReceipt['settlement_state'],
    charged_credits: r.charged_credits as number | null, physical_cessation_verified: r.physical_cessation_verified,
  };
}

export function codexStatusLabel(receipt: CodexReceipt): string {
  const labels: Record<CodexReceipt['status'], string> = {
    submitted: 'Investigation submitted', queued: 'Investigation queued', running: 'Investigating repository',
    completed: receipt.physical_cessation_verified ? 'Investigation completed' : 'Result received; verifying completion',
    failed: receipt.physical_cessation_verified ? 'Investigation failed' : 'Failure reported; verifying termination',
    cancel_requested: 'Cancellation requested; waiting for stop confirmation',
    cancelled: receipt.physical_cessation_verified ? 'Investigation stopped' : 'Cancellation reported; verifying stop',
    reconciliation_pending: 'Investigation status needs verification',
  };
  return labels[receipt.status];
}

/** An ambiguous start/cancel may have taken effect. Only the endpoint's explicit
 * no-effect errors may downgrade that uncertainty; never expose private errors. */
export function codexFailureState(status: number, value: unknown, operation: 'start' | 'status' | 'cancel'):
  'unavailable' | 'denied' | 'uncertain' | 'verification_unavailable' | 'in_progress' {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const failure = value as Record<string, unknown>;
    if (failure.qualified === false) {
      if (failure.inspect_required === true) return 'uncertain';
      if (failure.inspect_required === false) {
        if (status === 403 && failure.error === 'codex_session_forbidden') return 'denied';
        // Definite start refusals: nothing was created, so they are never "uncertain".
        if (operation === 'start' && status === 409 && failure.error === 'codex_session_in_progress') return 'in_progress';
        if (operation === 'start' && status === 409 && failure.error === 'codex_session_idempotency_conflict') return 'denied';
        if (status === 503 && failure.error === 'codex_session_unavailable') {
          return operation === 'status' ? 'verification_unavailable' : 'unavailable';
        }
      }
    }
  }
  return operation === 'status' ? 'verification_unavailable' : 'uncertain';
}
